import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

const produtos = [
  { icon: 'ri-armchair-line', title: 'Cadeiras Ergonômicas', desc: 'Frisokar e Plaxmetal NR-17 para escritórios e showrooms em Santa Cruz do Capibaribe.', link: '/cadeiras' },
  { icon: 'ri-layout-grid-line', title: 'Mesas Corporativas', desc: 'Estações de trabalho e mesas executivas Marzo Vitorino e Pandim.', link: '/mesas' },
  { icon: 'ri-layout-masonry-line', title: 'Divisórias Modulares', desc: 'Divisórias sem obra para escritórios e showrooms do polo de confecção.', link: '/divisorias' },
  { icon: 'ri-archive-line', title: 'Mobiliário de Aço', desc: 'Armários e estantes W3 para depósitos e escritórios industriais.', link: '/aco' },
  { icon: 'ri-volume-mute-line', title: 'Cabines Acústicas', desc: 'Privacidade para reuniões em ambientes de open office.', link: '/cabines-acusticas' },
  { icon: 'ri-tools-line', title: 'Projetos Turnkey', desc: 'Projeto completo de mobiliário para sua empresa em Santa Cruz.', link: '/turnkey' },
];

const diferenciais = [
  { icon: 'ri-truck-line', title: 'Entrega em Santa Cruz', desc: 'Entregamos e montamos em Santa Cruz do Capibaribe e toda a região do polo de confecção.' },
  { icon: 'ri-tools-line', title: 'Montagem inclusa', desc: 'Equipe técnica especializada para montagem no seu escritório ou showroom.' },
  { icon: 'ri-map-pin-line', title: 'Próximo de você', desc: 'Loja física em Caruaru, a cerca de 60 km de Santa Cruz do Capibaribe.' },
  { icon: 'ri-shield-check-line', title: 'Garantia de fábrica', desc: 'Todos os produtos com garantia de fábrica. Cadeiras com até 5 anos de garantia.' },
];

export default function MoveisCorporativosSantaCruzPage() {
  useSEO({
    title: 'Móveis Corporativos Santa Cruz do Capibaribe PE | Cadeiras e Mesas — Home Office Premium',
    description: 'Móveis corporativos em Santa Cruz do Capibaribe PE com entrega e montagem. Cadeiras ergonômicas NR-17, mesas corporativas e divisórias para empresas do polo de confecção. Atendemos Santa Cruz, Toritama e Agreste de PE.',
    keywords: 'móveis corporativos Santa Cruz do Capibaribe, cadeiras ergonômicas Santa Cruz Capibaribe, mesas corporativas Santa Cruz PE, mobiliário escritório polo confecção, cadeiras escritório Santa Cruz, Frisokar Santa Cruz Capibaribe, divisórias Santa Cruz, móveis Agreste PE, móveis corporativos polo têxtil',
    path: '/moveis-corporativos-santa-cruz-do-capibaribe',
    ogTitle: 'Móveis Corporativos Santa Cruz do Capibaribe PE | Home Office Premium',
    ogDescription: 'Móveis corporativos em Santa Cruz do Capibaribe PE com entrega e montagem. Cadeiras ergonômicas, mesas e divisórias.',
  });

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    const schemaId = 'schema-santacruz';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Home Office Premium — Móveis Corporativos Santa Cruz do Capibaribe',
      description: 'Móveis corporativos em Santa Cruz do Capibaribe PE. Cadeiras ergonômicas, mesas corporativas e divisórias para empresas do polo de confecção.',
      url: `${siteUrl}/moveis-corporativos-santa-cruz-do-capibaribe`,
      telephone: '+55-81-98203-0285',
      address: { '@type': 'PostalAddress', streetAddress: 'Av. Agamenon Magalhães, 1519', addressLocality: 'Caruaru', addressRegion: 'PE', postalCode: '55014-000', addressCountry: 'BR' },
      geo: { '@type': 'GeoCoordinates', latitude: -7.9558, longitude: -36.1025 },
      areaServed: [
        { '@type': 'City', name: 'Santa Cruz do Capibaribe' },
        { '@type': 'City', name: 'Toritama' },
        { '@type': 'City', name: 'Caruaru' },
        { '@type': 'City', name: 'Surubim' },
        { '@type': 'City', name: 'Vertentes' },
      ],
      serviceType: 'Venda e Instalação de Móveis Corporativos',
    });

    const faqId = 'schema-santacruz-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'A Home Office Premium entrega móveis corporativos em Santa Cruz do Capibaribe?', acceptedAnswer: { '@type': 'Answer', text: 'Sim. Entregamos e montamos móveis corporativos em Santa Cruz do Capibaribe e toda a região do polo de confecção do Agreste, incluindo Toritama e Caruaru.' } },
        { '@type': 'Question', name: 'Quais móveis são indicados para showrooms e escritórios em Santa Cruz do Capibaribe?', acceptedAnswer: { '@type': 'Answer', text: 'Para showrooms e escritórios do polo de confecção, recomendamos cadeiras ergonômicas NR-17, mesas operacionais, divisórias modulares para separar setores e armários de aço para documentos.' } },
        { '@type': 'Question', name: 'Qual o prazo de entrega para Santa Cruz do Capibaribe?', acceptedAnswer: { '@type': 'Answer', text: 'O prazo médio de entrega para Santa Cruz do Capibaribe é de 5 a 15 dias úteis, dependendo do produto. Nossa equipe realiza a montagem no local.' } },
      ],
    });

    return () => { script?.remove(); faqScript?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-20">
        <div className="relative bg-[#111111] overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://readdy.ai/api/search-image?query=modern%20corporate%20office%20interior%20Agreste%20Pernambuco%20Brazil%20textile%20industry%20professional%20workspace%20ergonomic%20furniture%20contemporary%20design%20warm%20lighting&width=1440&height=600&seq=santacruzhero2026&orientation=landscape" alt="Móveis corporativos em Santa Cruz do Capibaribe PE" className="w-full h-full object-cover object-top opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 to-black/40"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-[#E8610A]/20 border border-[#E8610A]/40 text-[#E8610A] text-xs font-semibold px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
                <i className="ri-map-pin-line"></i>
                Santa Cruz do Capibaribe — Polo de Confecção
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-5">
                Móveis Corporativos em<br />
                <span className="text-[#E8610A]">Santa Cruz do Capibaribe</span>
              </h1>
              <p className="text-white/70 text-lg max-w-xl leading-relaxed mb-8">
                Cadeiras ergonômicas, mesas corporativas e divisórias para empresas do polo de confecção. Entrega e montagem inclusos em Santa Cruz do Capibaribe e região.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://wa.me/5581982030285?text=Oi%2C%20sou%20de%20Santa%20Cruz%20do%20Capibaribe%20e%20quero%20um%20or%C3%A7amento%20de%20m%C3%B3veis%20corporativos" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white font-semibold px-8 py-4 rounded-full hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-whatsapp-line"></i>Solicitar Orçamento
                </a>
                <Link to="/contato" className="inline-flex items-center justify-center gap-2 bg-white/10 text-white font-semibold px-8 py-4 rounded-full border border-white/20 hover:bg-white/20 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-message-3-line"></i>Falar com Especialista
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Catálogo</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Produtos para Santa Cruz do Capibaribe</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {produtos.map(p => (
                <Link key={p.title} to={p.link} className="group bg-[#f8f8f6] rounded-2xl p-7 hover:bg-orange-50 transition-colors duration-300 cursor-pointer">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl mb-5 group-hover:bg-[#E8610A] transition-colors duration-300">
                    <i className={`${p.icon} text-[#E8610A] text-xl group-hover:text-white transition-colors duration-300`}></i>
                  </div>
                  <h3 className="text-gray-900 font-bold text-base mb-2">{p.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="py-20 bg-[#fafaf8]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {diferenciais.map(d => (
                <div key={d.title} className="bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#E8610A]/10 rounded-xl mb-4">
                    <i className={`${d.icon} text-[#E8610A] text-xl`}></i>
                  </div>
                  <h3 className="text-gray-900 font-semibold text-base mb-2">{d.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="py-16 bg-[#E8610A]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Orçamento para Santa Cruz do Capibaribe</h2>
            <p className="text-white/70 mb-8">Fale com nossa equipe e receba um projeto personalizado para sua empresa.</p>
            <a href="https://wa.me/5581982030285?text=Oi%2C%20sou%20de%20Santa%20Cruz%20do%20Capibaribe%20e%20quero%20um%20or%C3%A7amento%20de%20m%C3%B3veis%20corporativos" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-[#E8610A] font-semibold px-8 py-4 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-whatsapp-line"></i>Falar no WhatsApp
            </a>
          </div>
        </div>
      </div>
      <Footer />
      <a href="https://wa.me/5581982030285?text=Oi%2C%20sou%20de%20Santa%20Cruz%20do%20Capibaribe%20e%20quero%20um%20or%C3%A7amento%20de%20m%C3%B3veis%20corporativos" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp">
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
